<template>
  <div
    ref="dropdownContainer"
    class="dropdown__container"
    @keydown.escape.stop.prevent="close()"
  >
    <div class="input__before">
      <label
        v-if="label || $slots.label"
        class="label input__label"
        :for="`input-${data.inputId}`"
        @click="focus"
      >
        <slot name="label">{{ label }}</slot>
      </label>
      <slot name="top-right"></slot>
    </div>

    <div
      class="dropdown"
      v-bind="attrsWithoutClass"
      :class="[{ dropdown_inline: inline }, dropdownClass]"
      @click="setMouseEventHandled"
    >
      <!-- SEARCH INPUT -->
      <template v-if="searchable">
        <input
          v-show="data.isOpen"
          ref="searchfieldElement"
          v-model="data.searchString"
          :tabindex="searchable ? 0 : -1"
          type="search"
          class="dropdown__searchfield"
          :class="{
            '-result-selected': data.selectedKey != null,
            '-empty': data.searchString == ''
          }"
          :placeholder="data.selectedKey == null ? $t('labels.filterEntries') : ''"
          @keydown.down.prevent="onKeyDown($event)"
          @keydown.up.prevent="onKeyUp($event)"
          @keydown.enter.prevent="onSearchEnter"
        />
      </template>

      <!-- SELECTED OPTION -->
      <button
        :id="`input-${data.inputId}`"
        ref="mainButton"
        :tabindex="!data.isOpen ? 0 : searchable ? -1 : 0"
        :class="[{ 'is-open': data.isOpen, '-has-error': hasError }, buttonClass]"
        :aria-expanded="data.isOpen"
        aria-haspopup="menu"
        class="dropdown__closedbutton button--clear"
        type="button"
        @focusin.capture="onMainButtonFocusIn"
        @click="onMainButtonClick"
        @keydown.down.prevent="onKeyDown($event)"
        @keydown.up.prevent="onKeyUp($event)"
      >
        <span
          v-if="data.selectedKey == null || (clearOnSelect && !data.isOpen)"
          class="dropdown__closedbutton__text"
        >
          {{ placeholder != null ? placeholder : $t("labels.choose") + "..." }}
        </span>
        <span v-else class="dropdown__closedbutton__text">
          <slot :item="selected">
            <template v-if="prop != null">{{ selected?.[prop] }}</template>
            <template v-else>{{ selected }}</template>
          </slot>
        </span>
        <div class="dropdown__closedbutton__icon">
          <lucide-icon :name="data.isOpen ? 'chevron-up' : 'chevron-down'" />
        </div>
      </button>

      <!-- OPTIONS LIST -->
      <input-dropdown-options
        v-if="data.isOpen"
        ref="optionsElement"
        class="dropdown__options"
        :class="{ '-to-top': data.openToTop }"
        :options="options"
        :ordered-keys="orderedOptionKeys"
        :prop="prop"
        :search-string="data.searchString"
        :selected-key="data.selectedKey ?? undefined"
        :trap-focus="!searchable"
        :group-key="groupKey"
        :empty-text="emptyText"
        @focus-out="onOptionsFocusOut"
        @update:file-data="data.fileData = $event"
        @update:selected-key="setSelectedKey($event)"
        @update:hovered-option="data.hoveredOption = $event"
        @select="onSelect($event)"
        @close="close()"
      >
        <template #default="{ item }">
          <slot :item="item"></slot>
        </template>

        <template #before-options>
          <slot name="before-options" :close="close"></slot>
        </template>

        <template #grouplabel="{ value: groupLabelValue }">
          <slot name="grouplabel" :value="groupLabelValue"></slot>
        </template>

        <template #after-option="{ item }">
          <slot name="after-option" :item="item"></slot>
        </template>

        <template #after-options>
          <slot name="after-options" :close="close"></slot>
        </template>

        <template #empty>
          <slot name="empty"></slot>
        </template>

        <template v-if="mustConfirm" #footer>
          <!-- OPTIONS CONFIRMATION -->
          <button
            v-if="data.selectedKey != null"
            class="button"
            type="button"
            @click="confirmSelection(data.selectedKey)"
          >
            {{ $t("labels.choose") }}
          </button>
          <button
            v-if="data.selectedKey == null"
            class="button"
            type="button"
            @click="cancelSelection"
          >
            {{ $t("labels.abort") }}
          </button>
        </template>
      </input-dropdown-options>
    </div>
    <span
      v-if="error != '' && error != null"
      class="input__error message-error"
    >
      {{ error }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { map, findKey, filter } from "lodash-es";
import { computed, nextTick, onUnmounted, reactive, useAttrs, useTemplateRef, watch } from "vue";
import { v4 as uuid } from "uuid";
import { onClickOutside } from "@vueuse/core";

import InputDropdownOptions from "./InputDropdownOptions.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import { getScrollParent } from "@/helpers/utility/DOM";

import type { Handleable } from "@core/types/Events";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DropdownOption = any;

const props = withDefaults(defineProps<{
  options: { [key: string]: DropdownOption };
  select?: string | number;
  value?: DropdownOption;
  prop?: string | null;
  placeholder?: string | null;
  clearOnSelect?: boolean;
  mustConfirm?: boolean;
  emptyText?: string;
  buttonClass?: string | Record<string, boolean>;
  dropdownClass?: string | Record<string, boolean>;
  inline?: boolean;
  order?: string[] | number[];
  label?: string;
  required?: boolean;
  error?: string;
  searchable?: boolean;
  itemKey?: string | ((item: DropdownOption) => string);
  groupKey?: string;
}>(), {
  value: undefined,
  clearOnSelect: false,
  mustConfirm: false,
  inline: false,
  label: "",
  required: false,
  searchable: false,
  itemKey: "id",
});

const emit = defineEmits([
  "open",
  "hoveron",
  "hoveroff",
  "deselect",
  "preselect",
  "select",
  "update:value"
]);

const dropdownContainer = useTemplateRef("dropdownContainer");
const optionsElement = useTemplateRef("optionsElement");
const searchfieldElement = useTemplateRef("searchfieldElement");
const mainButtonElement = useTemplateRef("mainButton");

let removeOutsideClickListener: (() => void) | null | undefined = null;
onUnmounted(() => {
  if (removeOutsideClickListener) {
    removeOutsideClickListener();
    removeOutsideClickListener = null;
  }
});

const data = reactive({
  selectedKey: null as string | null,
  isOpen: false,
  fileData: null,
  parentScrollBeforeOpen: null as number | null,
  inputId: uuid(),
  hoveredOption: null,
  searchString: "",
  openToTop: false
});

const orderedOptionKeys = computed((): string[] => {
  if (props.order == null) {
    return Object.keys(props.options);
  }
  const orderMap = map(props.order, (itemKey) => {
    return findKey(
      props.options,
      (item) => getPropValue(item, props.itemKey) === itemKey
    );
  });
  return filter(orderMap, (key) => key != null) as string[];
});

const selected = computed(() => {
  if (data.selectedKey != null) return props.options[data.selectedKey];
  if (typeof props.value != "undefined") return props.value;
  return null;
});

const attrsWithoutClass = computed(() => {
  const attrs = { ...useAttrs() };
  delete attrs.class;
  return attrs;
});

const hasError = computed(() => {
  return props.error != null || selected.value?.disabled;
});

watch(
  () => props.value,
  (val) => {
    updateSelectedKeyFromValue(val);
  },
  { immediate: true }
);

watch(
  () => props.options,
  () => {
    if (props.value) {
      updateSelectedKeyFromValue(props.value);
    }
  }
);

watch(
  () => props.select,
  () => {
    // A defined value prop beats the select prop.
    if (typeof props.value != "undefined") return;

    if (props.select == null) {
      data.selectedKey = null;
    } else {
      data.selectedKey = `${props.select}`;
    }
  },
  { immediate: true }
);

watch(
  () => data.selectedKey,
  (value, oldValue) => {
    if (data.isOpen) {
      if (oldValue) {
        emit("deselect");
      }
      if (value) {
        emit("preselect", props.options[value]);
      } else {
        emit("deselect");
      }
    }
  },
  { immediate: true }
);

watch(
  () => data.hoveredOption,
  (hovered, oldHovered) => {
    if (oldHovered) emit("hoveroff", oldHovered);
    if (hovered) emit("hoveron", hovered);
  }
);

function setSelectedKey(value: string | number | null) {
  if (typeof value === "number") {
    value = `${value}`;
  }
  data.selectedKey = value;
}

function updateSelectedKeyFromValue(val: DropdownOption) {
  if (val && typeof val === "object") {
    const valKey = getPropValue(val, props.itemKey);
    if (valKey != null) {
      const index = findKey(
        props.options,
        (item) => getPropValue(item, props.itemKey) == valKey
      );
      data.selectedKey = `${index}`;
    } else {
      data.selectedKey = null;
    }
  } else {
    if (val != null) {
      const index = findKey(props.options, (item) => item == val);
      data.selectedKey = `${index}`;
    } else {
      data.selectedKey = null;
    }
  }
}

function onMainButtonClick(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.hasBeenHandled = true;
  if (data.isOpen) {
    close();
  } else {
    open();
  }
}

async function open() {
  data.isOpen = true;
  emit("open");
  
  
  // choose side to open the drowndown to
  // (top or bottom)
  if (dropdownContainer.value) {
    // save the scroll position before the dropdown opened
    const parentNode = getScrollParent(dropdownContainer.value.parentNode);

    const rect = dropdownContainer.value.getBoundingClientRect();
    let yThreshold = window.innerHeight * 0.75;
    if (parentNode) {
      const parentRect = parentNode.getBoundingClientRect();
      yThreshold = parentRect.top + parentRect.height * 0.70;
    }
    if (rect.bottom >= yThreshold) {
      data.openToTop = true;
    } else {
      data.openToTop = false;
    }

    if (parentNode) data.parentScrollBeforeOpen = parentNode.scrollTop;
    else data.parentScrollBeforeOpen = null;
  }


  // if this is a simple select, allow to cancel by clicking outside the select.
  if (!props.mustConfirm) {
    removeOutsideClickListener = onClickOutside(
      dropdownContainer.value,
      (event) => {
        console.log(event);
        close(event);
      }
    );
  }

  if (props.searchable) {
    // focus the selected option
    await nextTick();
    await nextTick(); // wait for the options focus to be set
    if (searchfieldElement.value) {
      searchfieldElement.value.focus();
    }
  }
}

function close(event?: Event) {
  data.isOpen = false;
  data.hoveredOption = null;
  data.searchString = "";

  if (props.clearOnSelect) {
    data.selectedKey = null;
  } else if (data.selectedKey == null) {
    if (typeof props.value != "undefined") {
      updateSelectedKeyFromValue(props.value);
    } else {
      if (props.select == null) {
        data.selectedKey = null;
      } else {
        data.selectedKey = `${props.select}`;
      }
    }
  }

  emit("deselect");

  if (event && event.type === "click" && event.target instanceof HTMLElement && !dropdownContainer.value?.contains(event.target)) {
    // do nothing.
  } else {
    nextTick(() => {
      focus();
    });
  }

  if (removeOutsideClickListener) {
    removeOutsideClickListener();
  }

  // scroll to the position we had before opening the dropdown:
  if (data.parentScrollBeforeOpen != null) {
    const parent = getScrollParent(dropdownContainer.value?.parentNode || null);
    const toTop = data.parentScrollBeforeOpen;
    data.parentScrollBeforeOpen = null;

    if (parent == null) return;

    // scroll up after closing if we scrolled past a threshold
    nextTick(() => {
      nextTick(() => {
        const minScrollLimit = 30;
        if (Math.abs(parent.scrollTop - toTop) > minScrollLimit) {
          parent.scrollTop = toTop;
        }
      });
    });
  }
}

function clear() {
  data.selectedKey = null;
}

function onSelect({ key, wasSelected }: { key: string | number; wasSelected: boolean }) {
  if (props.mustConfirm && !wasSelected) {
    return;
  }

  confirmSelection(key);
}

function confirmSelection(optionKey: string | number | null) {
  if (optionKey == null) return;

  const item = props.options[optionKey];

  close();
  const emitData = {
    key: optionKey,
    item: item,
    data: data.fileData
  };
  emit("select", emitData);
  emit("update:value", item);
  if (props.clearOnSelect) {
    clear();
  }
}

function cancelSelection() {
  close();
  data.selectedKey = null;
}

function focus() {
  const mainButton = mainButtonElement.value as HTMLButtonElement | null;
  if (mainButton) {
    mainButton.focus();
  }
}

function onKeyUp(event: KeyboardEvent) {
  event.preventDefault();

  if (data.isOpen) {
    optionsElement.value?.onKeyUp?.(event);
  } else {
    open();
  }
}

function onKeyDown(event: KeyboardEvent) {
  event.preventDefault();

  if (data.isOpen) {
    optionsElement.value?.onKeyDown?.(event);
  } else {
    open();
  }
}

function onMainButtonFocusIn() {
  if (data.isOpen && props.searchable) {
    nextTick(() => {
      if (searchfieldElement.value) {
        searchfieldElement.value.focus();
      }
    });
  }
}

function onSearchEnter() {
  if (data.selectedKey) {
    confirmSelection(data.selectedKey);
  } else {
    close();
  }
}

function onOptionsFocusOut() {
  if (props.searchable) {
    nextTick(() => {
      if (searchfieldElement.value) {
        searchfieldElement.value.focus();
      }
    });
  }
}


function setMouseEventHandled(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.hasBeenHandled = true;
}

function getPropValue<T extends object>(
  item: T,
  key: string | ((item: T) => string)
) {
  if (!item) return undefined;
  if (typeof key == "string") {
    return item[key as keyof T];
  }
  return key(item);
}

defineExpose({
  clear,
  close,
  focus,
  open
});
</script>

<style lang="scss">
.dropdown {
  width: 100%;
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 2.6rem;

  $padding-sides: 1rem;
  --padding-sides: 1rem;

  --padding-up-down: 0.5em;
  --min-padding-up-down: -0.5em;

  &__container {
    width: auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: min-content 1fr min-content;
    grid-template-areas: "before" "input" "after";
  }

  &__closedbutton {
    grid-row-start: 1;
    grid-row-end: 2;
    grid-column-start: 1;
    grid-column-end: 3;
    padding: 0;
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2.6rem;
    // display: inline-flex;
    align-items: stretch;
    justify-content: center;
    font-size: var(--font-size-button);
    line-height: 22px;
    font-weight: 700;
    text-transform: none;
    font-family: var(--font-family-ui);
    text-align: left;

    &:focus {
      outline: none;
      border-color: var(--color-action);
    }

    &__text {
      padding-left: var(--padding-sides, $padding-sides);
      padding-top: var(--padding-up-down);
      padding-bottom: var(--padding-up-down);
      padding-right: 4px;
      margin-right: auto;
      min-height: calc(1.7em + var(--padding-up-down) * 2);
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
      min-width: 0;

      .is-open > & {
        white-space: nowrap;
      }
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      border-left: 1px solid var(--color-border);
      padding-right: 0.5rem;
      flex-shrink: 0;
      font-size: 20px;
    }

    &.is-open {
      color: var(--color-action);
    }

    &.-has-error {
      border-color: var(--color-error);
    }
  }

  &__options {
    position: absolute;
    top: 100%;
    left: -1px;
    right: -1px;
    z-index: 12;

    &.-to-top {
      top: auto;
      bottom: 100%;
    }
  }

  &__searchfield {
    grid-row-start: 1;
    grid-row-end: 2;
    grid-column-start: 1;
    grid-column-end: 1;
    position: absolute;
    padding-left: 0;
    padding-top: calc(var(--padding-up-down) - 2px);
    padding-bottom: calc(var(--padding-up-down) - 2px);
    padding-right: 4px;
    top: 2px;
    bottom: 2px;
    left: var(--padding-sides);
    right: 2px;
    font-family: var(--font-family-ui);
    font-weight: 700;
    border: none;
    min-width: 0;
    line-height: 1.15;
    background-color: var(--color-background-close);
    cursor: default;
    pointer-events: none;
    opacity: 0;

    &:focus {
      outline: none;
      opacity: 1;
    }
    &::-webkit-search-cancel-button {
      display: none;
    }

    &:focus &.-result-selected {
      opacity: 1;
    }
  }
  &__searchfield:focus ~ &__closedbutton {
    border-color: var(--color-action);
  }

  &_inline {
    width: auto;
    display: inline-block;
    --padding-up-down: 0.1em;
    --min-padding-up-down: -0.1em;
    --padding-sides: 0.5em;
  }

  &-slim {
    --padding-up-down: 0.25em;
  }
}
</style>
